/**
 * ResponsiveImage, drop-in replacement for <img>.
 *
 * Strategy:
 * 1. If the input src is "/images/blog/foo.jpg" or "/images/foo.jpg", we
 *    automatically derive WebP siblings:
 *      - /images/blog/foo.webp      (1600w, desktop / hero)
 *      - /images/blog/foo-800.webp  (800w,  mobile / grid)
 *    AND we keep the original JPG as a graceful fallback inside <picture>.
 * 2. lazy loading by default (override with eager prop for above-the-fold hero).
 * 3. async decoding to keep the main thread free.
 *
 * Usage:
 *   <ResponsiveImage src="/images/blog/foo.jpg" alt="..." className="..." />
 *
 * If the path is not a JPG we know how to optimize (e.g. external URL,
 * existing PNG/SVG) we render a plain <img>.
 */
const KNOWN_OPTIMIZED_DIRS = ["/images/blog/", "/images/saint-malo-remparts"];

const buildVariants = (src) => {
  // Only optimize JPG paths from our public/images folder for which we
  // generated WebP siblings.
  if (!src || !src.endsWith(".jpg")) return null;
  const isOptimized =
    KNOWN_OPTIMIZED_DIRS.some((p) => src.startsWith(p) || src.includes(p));
  if (!isOptimized) return null;
  const base = src.slice(0, -4); // strip .jpg
  return {
    webpLarge: `${base}.webp`,
    webp800: `${base}-800.webp`,
    jpg: src,
  };
};

const ResponsiveImage = ({
  src,
  alt,
  className = "",
  eager = false,
  sizes = "(max-width: 768px) 100vw, 50vw",
  ...rest
}) => {
  const variants = buildVariants(src);
  const loading = eager ? "eager" : "lazy";
  const fetchPriority = eager ? "high" : "auto";

  if (!variants) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        loading={loading}
        decoding="async"
        fetchPriority={fetchPriority}
        {...rest}
      />
    );
  }

  return (
    <picture>
      <source
        type="image/webp"
        srcSet={`${variants.webp800} 800w, ${variants.webpLarge} 1600w`}
        sizes={sizes}
      />
      <img
        src={variants.jpg}
        alt={alt}
        className={className}
        loading={loading}
        decoding="async"
        fetchPriority={fetchPriority}
        {...rest}
      />
    </picture>
  );
};

export default ResponsiveImage;
